import React, { useState, useEffect } from 'react';
import s from './SelectPage.module.css';
import {
    selectProviderLoading,
    selectProviderPages,
} from '../../../store/selectors/socialSelectors';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store/store';
import { createSocialPage } from '../../../shared/api/socialApi';
import DropdownMenu from '../../../shared/ui/dropdown-menu/DropDownMenu';
import DropDownMenu from '../../../shared/ui/dropdown-menu/DropDownMenu';

type Option = {
    value: string;
    label: string;
};

type SelectPageProps = {
    value?: string;
    onChange?: (option: Option) => void;
    selectedSocial: null | string;
    placeholder?: string;
    source?: string;
};

const SelectPage: React.FC<SelectPageProps> = ({
    value,
    selectedSocial,
    onChange,
    placeholder = 'Select page',
    source = 'scrapping',
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const [selected, setSelected] = useState<Option | null>(null);
    const [inputValue, setInputValue] = useState('');

    const providerPages = useSelector((state: RootState) =>
        selectProviderPages(state, selectedSocial ?? ''),
    );

    const isLoading = useSelector((state: RootState) =>
        selectProviderLoading(state, selectedSocial ?? ''),
    );

    const options = providerPages.map((p) => ({
        value: p.id,
        label: p.name,
    }));

    useEffect(() => {
        if (value) {
            const match = options.find((opt) => opt.value === value) || null;
            setSelected(match);
        }
    }, [value, options]);

    const handleSelect = (option: Option) => {
        setSelected(option);
        if (onChange) onChange(option);
    };

    const handleCreate = async () => {
        if (!selectedSocial || inputValue.trim() === '') return;
        try {
            const action = await dispatch(
                createSocialPage({
                    provider: selectedSocial,
                    name: inputValue.trim(),
                }),
            );

            if (createSocialPage.fulfilled.match(action)) {
                const page = action.payload;
                const newOption = { value: page.id, label: page.name };
                handleSelect(newOption);
                setInputValue('');
            }
        } catch (err) {
            console.error('Failed to create board:', err);
        }
    };

    const textInfo = source === 'scrapping' ? 'analyze your profile' : 'post';

    return (
        <div className={s.wrapper}>
            {providerPages.length > 0 ? (
                <DropDownMenu
                    options={options}
                    selectedOption={selected?.label || ''}
                    setSelectedOption={(option) =>
                        handleSelect({
                            value: option,
                            label: options.find((opt) => opt.value === option)?.label || '',
                        })
                    }
                    placeholder={placeholder}
                    // width={350}
                />
            ) : (
                <div className={s.no_pages_found}>
                    <h3 className={s.no_pages_found_title}>
                        {selectedSocial === 'facebook'
                            ? 'No pages found'
                            : selectedSocial === 'pinterest'
                              ? 'No boards found'
                              : selectedSocial === 'linkedin'
                                ? 'No pages found'
                                : 'No pages found'}
                    </h3>
                    <p className={s.no_pages_found_text}>
                        {selectedSocial === 'facebook'
                            ? `To ${textInfo} on Facebook, you need to have available pages`
                            : selectedSocial === 'pinterest'
                              ? `To ${textInfo} on Pinterest, you need to have available boards`
                              : selectedSocial === 'linkedin'
                                ? `To ${textInfo} on LinkedIn, you need to have available pages`
                                : 'Please connect your social media account to continue'}
                    </p>
                </div>
            )}

            {selectedSocial === 'pinterest' && (
                <div className={s.creatable}>
                    <input
                        className={s.input}
                        type="text"
                        placeholder="Create new board..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                    />
                    <button disabled={isLoading || inputValue.trim() === ''} onClick={handleCreate}>
                        {isLoading ? 'Creating...' : 'Create'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default SelectPage;
